// Zoom-aware geometry for body-portaled, position:fixed popovers (dropdowns, hover menus, previews).
//
// NOTE for catchup-app-web: the learner app does NOT ship the "Yazı Boyutu" preference today, so
// --app-font-scale is unset here and every helper below is an exact no-op (getUiZoom() returns 1).
// It is carried anyway so this file and useImageZoomPan stay byte-identical with catchup-admin-web —
// these two files were identical before and the zoom-unit correction is precisely the kind of detail
// that would otherwise be fixed only in whichever copy someone happened to be looking at. If the
// preference ever lands here, the image zoom is already correct rather than subtly wrong.
//
// The app's "Yazı Boyutu" preference applies `zoom: var(--app-font-scale)` on <html> (see index.css /
// App.tsx). Under a zoom Z != 1:
//   • getBoundingClientRect() returns VISUAL coordinates (already multiplied by Z).
//   • a position:fixed element portaled into document.body is itself inside the zoomed <html>, so the
//     top/left we set are LOCAL (pre-zoom) px that the browser then multiplies by Z to paint.
// So to land a fixed popover at the same visual spot as its anchor, the rect-derived coordinates (and
// the viewport extents used for edge-clamping) must be DIVIDED by Z. Element offsetWidth/offsetHeight
// are already local px and must NOT be divided. At Z === 1 every helper is an exact no-op.

/** Current UI zoom factor from --app-font-scale (1 when unset/invalid). */
export const getUiZoom = (): number => {
  if (typeof document === "undefined") return 1;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(
    "--app-font-scale",
  );
  const value = parseFloat(raw);
  return value && value > 0 ? value : 1;
};

export interface ZoomRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

/** getBoundingClientRect() expressed in the LOCAL (pre-zoom) coordinate space used by fixed popovers. */
export const getZoomAdjustedRect = (element: Element): ZoomRect => {
  const rect = element.getBoundingClientRect();
  const zoom = getUiZoom();
  return {
    left: rect.left / zoom,
    top: rect.top / zoom,
    right: rect.right / zoom,
    bottom: rect.bottom / zoom,
    width: rect.width / zoom,
    height: rect.height / zoom,
  };
};

/** Viewport extents in the LOCAL (pre-zoom) coordinate space — use for edge-clamping fixed popovers. */
export const getZoomAdjustedViewport = (): { width: number; height: number } => {
  const zoom = getUiZoom();
  return {
    width: window.innerWidth / zoom,
    height: window.innerHeight / zoom,
  };
};
